import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createInvite, createInvitesBulk, listInvites, resendInvite } from '../services/invites.service';

// Pending + used invites for the current admin's scope. Key is ['invites'] per the query-key convention.
export function useInvitesQuery() {
  return useQuery({
    queryKey: ['invites'],
    queryFn: listInvites,
    // Changes with the admin's own actions (create/resend), which already invalidate this key —
    // a 30s window keeps revisits instant without masking those mutations.
    staleTime: 30_000,
  });
}

// Create an invite, then refresh the list so the new pending row appears immediately.
export function useCreateInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInvite,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

// Resend an invite's email; refresh the list so the bumped expiry shows immediately.
export function useResendInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resendInvite(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}

// Invite a roster in one call. Same invalidation as the single create — the whole batch lands as
// pending rows on the Invites list.
export function useBulkInvite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createInvitesBulk,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invites'] });
    },
  });
}
